import { useMemo, useState, type CSSProperties } from 'react';
import { RiMoreLine } from '@remixicon/react';
import {
  Button,
  EmptyState,
  ErrorState,
  Group,
  Notice,
  Row,
  SearchField,
  Segmented,
  StatusPill,
} from '@store/ui';
import { ApiError, formatMoney } from '../../api';
import { haptic } from '../../telegram';
import { ListSkeleton, useAsync } from '../shared';
import {
  adminCategories,
  adminProducts,
  duplicateProduct,
  newIdempotencyKey,
  PRODUCT_STATUS_LABELS,
  setProductStatus,
  type AdminProductRow,
  type ProductStatus,
} from './client';
import { blockingIssues } from './diagnose';
import { AdminHeader } from './fields';

// --- Товары ------------------------------------------------------------------

type Filter = 'all' | ProductStatus;

/* Mutable because `Segmented` takes a plain array; nothing writes to it. */
const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'published', label: 'Опубликованы' },
  { value: 'draft', label: 'Черновики' },
  { value: 'hidden', label: 'Скрытые' },
  { value: 'archived', label: 'Архив' },
];

const STATUS_TONE: Record<ProductStatus, 'neutral' | 'success' | 'warning'> = {
  draft: 'neutral',
  published: 'success',
  hidden: 'warning',
  archived: 'neutral',
};

/** Letters and digits; everything else in a title is punctuation or an emoji. */
const MARK_CHAR = /[\p{L}\p{N}]/u;

/*
 * The geometry of the two new pieces on this row lives here rather than in the
 * stylesheet, for the reason `ServiceTile` gives for its own: it is what keeps a
 * stranger's picture from breaking the row, and what keeps the actions button
 * out of the title's way. Face, colour and pressed state remain the
 * stylesheet's — the class names are emitted for it and the row is already
 * correct without them.
 */
const MARK: CSSProperties = {
  width: 44,
  height: 44,
  flexShrink: 0,
  borderRadius: 10,
  overflow: 'hidden',
  display: 'grid',
  placeItems: 'center',
};
const MARK_IMAGE: CSSProperties = { width: '100%', height: '100%', objectFit: 'cover' };
/* Absolute, so the title wraps against the full width of the row and does not
   reserve a column for a control most rows never show. */
const MORE: CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  width: 40,
  height: 44,
  display: 'grid',
  placeItems: 'center',
  background: 'transparent',
  border: 0,
  color: 'var(--text-tertiary)',
  cursor: 'pointer',
};
const ROW_BOX: CSSProperties = { position: 'relative' };
/* The title must not run under the actions button. */
const ROW_TITLE_ROOM: CSSProperties = { paddingRight: 32 };
const WARN: CSSProperties = { color: 'var(--danger)', marginTop: 2 };

/**
 * The picture, or the initials standing in for it.
 *
 * `GET /api/admin/products` does not send a picture today — it projects the
 * product row and never joins `product_media` — so the initials are the ordinary
 * case here, not a failure. Same shape as the home screen's service tile, which
 * is where the owner has already seen this fallback.
 */
function ProductMark({
  title,
  imageUrl,
}: {
  readonly title: string;
  readonly imageUrl?: string | null;
}) {
  const [failed, setFailed] = useState(false);
  const src = imageUrl?.trim() || null;

  const initials = title
    .trim()
    .split(/\s+/)
    .map((word) => [...word].find((char) => MARK_CHAR.test(char)))
    .filter((char): char is string => char !== undefined)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <span className="product__mark" style={MARK} aria-hidden="true">
      {src && !failed ? (
        <img
          className="product__markimage"
          style={MARK_IMAGE}
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="product__monogram caption">{initials || '—'}</span>
      )}
    </span>
  );
}

/** «3 варианта» — a bare numeral beside a title says nothing about what it counts. */
function variantLabel(count: number): string {
  const teens = count % 100;
  const last = count % 10;
  const noun =
    teens >= 11 && teens <= 14
      ? 'вариантов'
      : last === 1
        ? 'вариант'
        : last >= 2 && last <= 4
          ? 'варианта'
          : 'вариантов';
  // A non-breaking space, as `itemCountLabel` in `@store/ui` does it: the
  // numeral must not be left at the end of a line with its noun on the next.
  return `${count}\u00a0${noun}`;
}

export function AdminProductsScreen({
  onNavigate,
}: {
  readonly onNavigate: (route: string) => void;
}) {
  const list = useAsync(() => adminProducts(), []);
  const categories = useAsync(() => adminCategories(), []);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  /* Per row rather than per screen: publishing one product must not put a
     spinner on the four beside it, and the list stays usable while it runs. */
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<string | null>(null);
  /* What the server said about a change that SUCCEEDED — publishing something
     nobody can buy is allowed and answered with a warning, and that answer used
     to be discarded along with the route that produces it. */
  const [notice, setNotice] = useState<{ id: string; message: string } | null>(null);

  const categoryTitles = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of categories.data?.categories ?? []) map.set(category.id, category.title);
    return map;
  }, [categories.data]);

  const products = list.data?.products ?? [];

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return products.filter((product) => {
      if (filter !== 'all' && product.status !== filter) return false;
      if (!needle) return true;
      return product.title.toLowerCase().includes(needle);
    });
  }, [products, filter, query]);

  /**
   * A write that changes one row, followed by a re-read of the list.
   *
   * `list.reload()` and not `window.location.reload()`: the Mini App reboots
   * through Telegram's own launch sequence, which costs a second and drops the
   * owner back at the top of the list they were halfway down.
   */
  const mutate = async (id: string, task: () => Promise<unknown>) => {
    if (busyId) return;
    setBusyId(id);
    setRowError(null);
    setNotice(null);
    try {
      haptic.impact('light');
      await task();
      haptic.success();
      list.reload();
    } catch (cause) {
      haptic.error();
      setRowError({
        id,
        // The server's own sentence. It knows why it refused; this screen does not.
        message: cause instanceof ApiError ? cause.message : 'Не удалось сохранить изменение.',
      });
    } finally {
      setBusyId(null);
    }
  };

  /**
   * A copy, as a draft.
   *
   * Never published: a duplicate carries the original's price and description
   * but none of its stock, so publishing one automatically would put an
   * unbuyable product in the shop the instant the owner pressed «Дублировать».
   * The variants come across because a product's options are most of what makes
   * it worth copying — and a product with none stays a product with none.
   */
  const duplicate = async (row: AdminProductRow) => {
    await mutate(row.id, async () => {
      const { product } = await duplicateProduct(
        row.id,
        newIdempotencyKey(),
        `${row.title} (копия)`,
      );
      onNavigate(`/admin/products/${product.id}`);
    });
  };

  /**
   * Publish, hide, archive — and say what the server said back.
   *
   * The route answers with more than "done": it reports whether the product it
   * just published can actually be bought, and refusing to publish an unsellable
   * one would be a dead end the owner could not get out of from a phone. So the
   * change goes through and the answer is shown.
   */
  const changeStatus = async (row: AdminProductRow, status: ProductStatus) => {
    await mutate(row.id, async () => {
      const result = await setProductStatus(row.id, status);
      if (result.warning?.message) setNotice({ id: row.id, message: result.warning.message });
    });
  };

  if (list.error) {
    return (
      <div className="container stack">
        <AdminHeader title="Товары" />
        <ErrorState title="Каталог не загрузился" text={list.error} onRetry={list.reload} />
      </div>
    );
  }

  return (
    <div className="container stack">
      <AdminHeader
        title="Товары"
        subtitle={list.loading ? null : `${products.length} в каталоге`}
      />

      <Group>
        <Row
          title="Категории"
          subtitle="Разделы, из которых собрана главная"
          chevron
          onClick={() => onNavigate('/admin/categories')}
        />
      </Group>

      <SearchField
        value={search}
        onChange={(value) => {
          setSearch(value);
          setQuery(value);
        }}
        onSubmit={() => setQuery(search)}
        placeholder="Название товара"
        label="Поиск по товарам"
      />

      <Segmented options={FILTERS} value={filter} onChange={setFilter} label="Фильтр по статусу" />

      {list.loading ? (
        <ListSkeleton rows={4} />
      ) : shown.length === 0 ? (
        <EmptyState
          title={products.length === 0 ? 'Товаров пока нет' : 'Ничего не нашлось'}
          text={
            products.length === 0
              ? 'Добавьте первый — его можно оставить черновиком и опубликовать позже.'
              : 'Попробуйте другое название или снимите фильтр.'
          }
        />
      ) : (
        <Group>
          {shown.map((product) => {
            const issues = blockingIssues({
              status: product.status,
              tracksInventory: product.tracksInventory,
              fulfillmentMode: product.fulfillmentMode,
              basePriceMinor: product.basePrice.amount,
              categoryId: product.categoryId,
              available: product.available,
              variants: product.variants,
            });
            const open = expanded === product.id;
            const busy = busyId === product.id;
            const category = product.categoryId
              ? (categoryTitles.get(product.categoryId) ?? 'Категория удалена')
              : 'Без категории';

            return (
              <div key={product.id}>
                <div className="adminproduct" style={ROW_BOX}>
                  <button
                    type="button"
                    className="product"
                    onClick={() => onNavigate(`/admin/products/${product.id}`)}
                  >
                    <ProductMark title={product.title} imageUrl={product.imageUrl} />
                    <span className="product__body">
                      <span className="product__title" style={ROW_TITLE_ROOM}>
                        {product.title}
                      </span>
                      <span className="product__subtitle">
                        {category} ·{' '}
                        {product.tracksInventory
                          ? `остаток ${product.available ?? 0}`
                          : 'без учёта остатков'}
                        {product.variants.length > 0
                          ? ` · ${variantLabel(product.variants.length)}`
                          : ''}
                      </span>
                      <span className="product__meta">
                        <span className="price">{formatMoney(product.basePrice)}</span>
                        <StatusPill tone={STATUS_TONE[product.status]}>
                          {PRODUCT_STATUS_LABELS[product.status]}
                        </StatusPill>
                        {issues.length > 0 ? (
                          <StatusPill tone="danger" icon="alert">
                            Не купить
                          </StatusPill>
                        ) : null}
                      </span>
                      {/*
                       * The reason, on the row itself. A red pill says that
                       * something is wrong; only the sentence says what, and
                       * this is the screen where it can still be fixed.
                       *
                       * Two of them, then a count. A product can be broken four
                       * ways at once, and four red lines per row turns a
                       * catalogue that needs attention into a wall nobody
                       * reads — the rest are waiting in the editor.
                       */}
                      {issues.slice(0, 2).map((issue) => (
                        <span key={issue} className="caption product__warn" style={WARN}>
                          {issue}
                        </span>
                      ))}
                      {issues.length > 2 ? (
                        <span className="caption product__warn" style={WARN}>
                          И ещё {issues.length - 2} — откройте товар.
                        </span>
                      ) : null}
                    </span>
                  </button>
                  {/* A sibling, not a child: `.product` is itself a button and a
                      button inside a button is not a thing a browser renders. */}
                  <button
                    type="button"
                    className="adminproduct__more"
                    style={MORE}
                    aria-label={open ? 'Скрыть действия' : `Действия: ${product.title}`}
                    aria-expanded={open}
                    onClick={() => {
                      haptic.select();
                      setConfirmArchive(null);
                      setExpanded(open ? null : product.id);
                    }}
                  >
                    <RiMoreLine size={20} aria-hidden="true" />
                  </button>
                </div>

                {open ? (
                  <>
                    <Row
                      icon="receipt"
                      title="Редактировать"
                      onClick={() => onNavigate(`/admin/products/${product.id}`)}
                    />
                    {product.status === 'published' ? (
                      <Row
                        icon="bag"
                        title="Открыть как покупатель"
                        onClick={() => onNavigate(`/product/${product.shortId}`)}
                      />
                    ) : null}
                    {product.tracksInventory ? (
                      <Row
                        icon="key"
                        title="Загрузить остатки"
                        subtitle={`Сейчас ${product.available ?? 0}`}
                        onClick={() => onNavigate(`/admin/products/${product.id}/stock`)}
                      />
                    ) : null}
                    <Row
                      icon="copy"
                      title="Дублировать"
                      subtitle="Копия создаётся черновиком, без остатков"
                      disabled={busy}
                      onClick={() => void duplicate(product)}
                    />
                    {product.status === 'published' ? (
                      <Row
                        icon="close"
                        title="Скрыть из магазина"
                        disabled={busy}
                        onClick={() => void changeStatus(product, 'hidden')}
                      />
                    ) : (
                      <Row
                        icon="circleCheck"
                        title="Опубликовать"
                        {...(issues.length > 0
                          ? { subtitle: 'Проверьте предупреждения выше' }
                          : {})}
                        disabled={busy || product.status === 'archived'}
                        onClick={() => void changeStatus(product, 'published')}
                      />
                    )}
                    {product.status !== 'archived' ? (
                      /* Two taps, because there is no undo: the second row is
                         the confirmation, in place, rather than a dialog that
                         Telegram would draw over the whole app. */
                      <Row
                        icon="inbox"
                        title={
                          confirmArchive === product.id ? 'Точно в архив?' : 'Перенести в архив'
                        }
                        {...(confirmArchive === product.id
                          ? { subtitle: 'Товар исчезнет из магазина. Нажмите ещё раз.' }
                          : {})}
                        disabled={busy}
                        onClick={() => {
                          if (confirmArchive !== product.id) {
                            haptic.impact('light');
                            setConfirmArchive(product.id);
                            return;
                          }
                          setConfirmArchive(null);
                          void changeStatus(product, 'archived');
                        }}
                      />
                    ) : null}
                    {notice?.id === product.id ? (
                      /* The change went through — this is the server telling the
                         owner what it now looks like to a customer. */
                      <div style={{ padding: 'var(--space-3) var(--space-4)' }}>
                        <Notice tone="warning">{notice.message}</Notice>
                      </div>
                    ) : null}
                    {rowError?.id === product.id ? (
                      <div style={{ padding: 'var(--space-3) var(--space-4)' }}>
                        <Notice tone="warning">{rowError.message}</Notice>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            );
          })}
        </Group>
      )}

      <div className="action-bar">
        <Button block onClick={() => onNavigate('/admin/products/new')}>
          Добавить товар
        </Button>
      </div>
    </div>
  );
}
